/**
 * MYAC-882 — single source of truth for the main-chat FAQ-style prompt list.
 *
 * Each group mirrors a tool family of the deployed main chat agent (chatbot
 * `chat_35flash` agent — see its `agent.py` tool list). When the agent's
 * toolset changes, update this list together with the `chat.suggestions.*`
 * keys in `en.json` / `fr.json`:
 *  - `documents`  — search/find/browse/inspect/read documents, read_file_by_name (MYAC-848)
 *  - `registers`  — get_qms_register + link_registers grounding, gated
 *                   trigger_register_refresh (MYAC-815) and record append/delete (MYAC-848);
 *                   covers all four registers incl. Change Control (MYAC-927)
 *  - `assessment` — assessment overview/topic/question/task tools (MYAC-831)
 *  - `technical_documentation` — TD/Product read tools: list_td_products,
 *                   get_td_overview/section/requirement/extraction_field, list/get_td_task (MYAC-835)
 *  - `standards`  — list_standards/list_clauses/get_clause_text + knowledge base tools
 *  - `attachments` — MYAC-1182: files dropped on the chat (list_chat_attachments,
 *                   read_attached_document, instant same-turn read; auto-saved to the
 *                   project documents in background since 2026-09-11)
 *  - `writing`    — MYAC-1161/1162: write_authored_document (draft + mode="edit"),
 *                   versioned authored documents
 *  - `board`      — MYAC-1127/776: propose_task_change / propose_task_lifecycle —
 *                   card-confirmed task edits, creation, lifecycle
 *  - `requirements_assessment` — MYAC-917 page-aware seed for the referentials pages
 *                   (the embedded companion chat's prompts, ported to the docked panel);
 *                   MAC can't answer these yet (Arslene's access work, in flight) — dev-only
 *                   exposure meanwhile, see spec.md §4
 */
export interface ChatSuggestion {
    /** Stable key — ties the prompt to an agent tool (family). */
    key: string
    /** i18n key of the example prompt. */
    promptKey: string
    /**
     * 'send' fires the prompt immediately on click; 'prefill' only puts it in
     * the textbox — for prompts the user must complete (e.g. a document name).
     */
    action: 'send' | 'prefill'
}

export interface ChatSuggestionGroup {
    key: string
    titleKey: string
    /** i18n key of the one-line capability explanation (help dialog). */
    descriptionKey: string
    suggestions: ChatSuggestion[]
}

export const CHAT_SUGGESTION_GROUPS: ChatSuggestionGroup[] = [
    {
        key: 'documents',
        titleKey: 'chat.suggestions.documents.title',
        descriptionKey: 'chat.suggestions.documents.description',
        suggestions: [
            { key: 'search', promptKey: 'chat.suggestions.documents.search', action: 'send' },
            { key: 'summarize', promptKey: 'chat.suggestions.documents.summarize', action: 'prefill' },
        ],
    },
    {
        key: 'registers',
        titleKey: 'chat.suggestions.registers.title',
        descriptionKey: 'chat.suggestions.registers.description',
        suggestions: [
            { key: 'status', promptKey: 'chat.suggestions.registers.status', action: 'send' },
            { key: 'links', promptKey: 'chat.suggestions.registers.links', action: 'send' },
            { key: 'changes', promptKey: 'chat.suggestions.registers.changes', action: 'send' },
            { key: 'refresh', promptKey: 'chat.suggestions.registers.refresh', action: 'send' },
            { key: 'records', promptKey: 'chat.suggestions.registers.records', action: 'prefill' },
        ],
    },
    {
        key: 'assessment',
        titleKey: 'chat.suggestions.assessment.title',
        descriptionKey: 'chat.suggestions.assessment.description',
        suggestions: [
            { key: 'summary', promptKey: 'chat.suggestions.assessment.summary', action: 'send' },
            { key: 'tasks', promptKey: 'chat.suggestions.assessment.tasks', action: 'send' },
        ],
    },
    {
        key: 'technical_documentation',
        titleKey: 'chat.suggestions.technical_documentation.title',
        descriptionKey: 'chat.suggestions.technical_documentation.description',
        suggestions: [
            { key: 'overview', promptKey: 'chat.suggestions.technical_documentation.overview', action: 'send' },
            { key: 'requirements', promptKey: 'chat.suggestions.technical_documentation.requirements', action: 'send' },
            { key: 'tasks', promptKey: 'chat.suggestions.technical_documentation.tasks', action: 'send' },
        ],
    },
    {
        key: 'standards',
        titleKey: 'chat.suggestions.standards.title',
        descriptionKey: 'chat.suggestions.standards.description',
        suggestions: [
            { key: 'clause', promptKey: 'chat.suggestions.standards.clause', action: 'send' },
            { key: 'knowledge', promptKey: 'chat.suggestions.standards.knowledge', action: 'send' },
        ],
    },
    {
        key: 'attachments',
        titleKey: 'chat.suggestions.attachments.title',
        descriptionKey: 'chat.suggestions.attachments.description',
        suggestions: [
            { key: 'list', promptKey: 'chat.suggestions.attachments.list', action: 'send' },
            // Needs a file actually attached — prefill so the user drops one first.
            { key: 'analyze', promptKey: 'chat.suggestions.attachments.analyze', action: 'prefill' },
        ],
    },
    {
        key: 'writing',
        titleKey: 'chat.suggestions.writing.title',
        descriptionKey: 'chat.suggestions.writing.description',
        suggestions: [
            { key: 'draft', promptKey: 'chat.suggestions.writing.draft', action: 'prefill' },
            { key: 'edit', promptKey: 'chat.suggestions.writing.edit', action: 'prefill' },
        ],
    },
    {
        key: 'board',
        titleKey: 'chat.suggestions.board.title',
        descriptionKey: 'chat.suggestions.board.description',
        suggestions: [
            { key: 'urgent', promptKey: 'chat.suggestions.board.urgent', action: 'send' },
            { key: 'create', promptKey: 'chat.suggestions.board.create', action: 'prefill' },
        ],
    },
    {
        key: 'requirements_assessment',
        titleKey: 'chat.suggestions.requirements_assessment.title',
        descriptionKey: 'chat.suggestions.requirements_assessment.description',
        suggestions: [
            { key: 'summary_first', promptKey: 'chat.suggestions.requirements_assessment.summary_first', action: 'send' },
            { key: 'deadlines', promptKey: 'chat.suggestions.requirements_assessment.deadlines', action: 'send' },
        ],
    },
]

/** MYAC-1329 — the opening state never shows more chips than this, on any surface. */
export const OPENING_SUGGESTION_LIMIT = 5

/**
 * MYAC-1329 — which families each project type's chat opens on. One place, because
 * three surfaces must agree: the chat page itself, and the docked panel on that
 * page's saved-prompts children (where the page's own chat is not rendered).
 *
 *  - `td`  (MYAC-835) leads with Technical Documentation and keeps only what works in
 *          a product context; register/assessment data lives on the QMS side. It
 *          narrows the capabilities popup too.
 *  - `qms` is the mirror with TD dropped. It narrows the OPENING only: the popup keeps
 *          every family, since drafting, attachments and the rest do work on a QMS.
 */
export function chatPageSuggestionProps(projectType: 'qms' | 'td'): {
    suggestionGroupKeys?: string[]
    openingGroupKeys?: string[]
} {
    return projectType === 'td'
        ? { suggestionGroupKeys: ['technical_documentation', 'documents', 'standards'] }
        : { openingGroupKeys: ['registers', 'assessment', 'documents', 'standards', 'board'] }
}

const shuffle = <T>(items: T[]): T[] => [...items].sort(() => Math.random() - 0.5)

/**
 * The chips an empty session opens on — a light sample, never more than
 * OPENING_SUGGESTION_LIMIT; the popup keeps the full grouped list.
 *
 * Unscoped: one random prompt per family, shuffled, cut to the limit.
 *
 * Scoped (the first group is the page's lead):
 *  - every family fits → one chip each, and the room left goes to more of the lead
 *    (a product chat: its three TD prompts, then documents and standards);
 *  - they don't all fit (the docked panel on a registers or referentials page, which
 *    leads one family over all of them) → the lead's prompts first, the rest as filler.
 *
 * Keys are namespaced by group — as siblings in one v-for, suggestion keys must be
 * unique ACROSS groups, which this file doesn't guarantee.
 */
export function pickOpeningSuggestions(
    groups: ChatSuggestionGroup[],
    { scoped }: { scoped: boolean },
): ChatSuggestion[] {
    const nonEmpty = groups.filter(g => g.suggestions.length > 0)
    const keyed = (g: ChatSuggestionGroup, s: ChatSuggestion): ChatSuggestion => ({ ...s, key: `${g.key}:${s.key}` })
    const onePerGroup = (gs: ChatSuggestionGroup[]) =>
        gs.map(g => keyed(g, g.suggestions[Math.floor(Math.random() * g.suggestions.length)]))

    if (!scoped || nonEmpty.length === 0) {
        return shuffle(onePerGroup(nonEmpty)).slice(0, OPENING_SUGGESTION_LIMIT)
    }
    const [lead, ...rest] = nonEmpty
    const leadRoom = rest.length < OPENING_SUGGESTION_LIMIT
        ? OPENING_SUGGESTION_LIMIT - rest.length
        : OPENING_SUGGESTION_LIMIT
    // A random subset when the lead can't show everything, kept in its authored order.
    const leadPicks = new Set(shuffle(lead.suggestions).slice(0, leadRoom))
    const leadChips = lead.suggestions.filter(s => leadPicks.has(s)).map(s => keyed(lead, s))
    return [...leadChips, ...shuffle(onePerGroup(rest))].slice(0, OPENING_SUGGESTION_LIMIT)
}
